class BlendsComparisonChartCarousel extends HTMLElement {
  connectedCallback() {
    this.rowSyncFrame = null;
    this.isNativeMobileSlider = false;
    this.mobileSlideWidth = 140;
    this.mobileSlideGap = 16;
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleResizeObservation = this.handleResizeObservation.bind(this);
    this.handleNativeScroll = this.handleNativeScroll.bind(this);
    this.handlePreviousClick = this.handlePreviousClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);

    this.swiperElement = this.querySelector('.blends-comparison-chart__swiper');
    this.wrapperElement = this.querySelector('.blends-comparison-chart__wrapper');
    this.previousButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.blends-comparison-chart__nav-button--prev')
    );
    this.nextButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.blends-comparison-chart__nav-button--next')
    );
    this.paginationElement = /** @type {HTMLDivElement | null} */ (
      this.querySelector('.blends-comparison-chart__pagination')
    );
    this.slideCount = this.querySelectorAll('.blends-comparison-chart__slide').length;
    this.hasMultipleBlends = this.slideCount > 1;
    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');

    if (this.mobileMediaQuery.addEventListener) {
      this.mobileMediaQuery.addEventListener('change', this.handleViewportChange);
    } else {
      this.mobileMediaQuery.addListener(this.handleViewportChange);
    }

    if (this.previousButton) {
      this.previousButton.addEventListener('click', this.handlePreviousClick);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.handleResizeObservation);
      this.resizeObserver.observe(this);
    }

    window.requestAnimationFrame(this.handleViewportChange);
  }

  disconnectedCallback() {
    this.disableNativeMobileSlider();
    this.resetRowHeights();

    if (this.rowSyncFrame) {
      window.cancelAnimationFrame(this.rowSyncFrame);
      this.rowSyncFrame = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.previousButton) {
      this.previousButton.removeEventListener('click', this.handlePreviousClick);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }

    if (!this.mobileMediaQuery) return;

    if (this.mobileMediaQuery.removeEventListener) {
      this.mobileMediaQuery.removeEventListener('change', this.handleViewportChange);
    } else {
      this.mobileMediaQuery.removeListener(this.handleViewportChange);
    }
  }

  handleViewportChange() {
    if (!this.mobileMediaQuery) return;

    this.syncRowHeights();

    if (!this.hasMultipleBlends || !this.swiperElement) {
      this.classList.remove('blends-comparison-chart__carousel-shell--fit');
      this.disableNativeMobileSlider();
      this.updateNavigationState();
      return;
    }

    if (this.mobileMediaQuery.matches) {
      this.classList.remove('blends-comparison-chart__carousel-shell--fit');
      this.enableNativeMobileSlider();
      return;
    }

    this.classList.remove('blends-comparison-chart__carousel-shell--fit');
    this.disableNativeMobileSlider();
    this.updateNavigationState();
  }

  handleResizeObservation() {
    this.handleViewportChange();
  }

  enableNativeMobileSlider() {
    if (!this.swiperElement) return;

    if (!this.isNativeMobileSlider) {
      this.isNativeMobileSlider = true;
      this.swiperElement.classList.add('blends-comparison-chart__swiper--native');
      this.swiperElement.addEventListener('scroll', this.handleNativeScroll, { passive: true });
    }

    this.syncRowHeights();
    this.updateNavigationState();
  }

  disableNativeMobileSlider() {
    if (!this.swiperElement || !this.isNativeMobileSlider) return;

    this.isNativeMobileSlider = false;
    this.swiperElement.classList.remove('blends-comparison-chart__swiper--native');
    this.swiperElement.removeEventListener('scroll', this.handleNativeScroll);
    this.swiperElement.scrollLeft = 0;
  }

  handleNativeScroll() {
    this.updateNavigationState();
  }

  /**
   * @param {Event} event
   */
  handlePreviousClick(event) {
    event.preventDefault();
    this.scrollBySlide(-1);
  }

  /**
   * @param {Event} event
   */
  handleNextClick(event) {
    event.preventDefault();
    this.scrollBySlide(1);
  }

  /**
   * @param {number} direction
   */
  scrollBySlide(direction) {
    if (!this.swiperElement || !this.isNativeMobileSlider) return;

    const step = this.getMobileSlideStep();
    this.swiperElement.scrollBy({
      left: direction * step,
      behavior: 'smooth',
    });
  }

  getMobileSlideStep() {
    const fallbackStep = (this.mobileSlideWidth || 0) + (this.mobileSlideGap || 0);

    if (!this.wrapperElement) {
      return fallbackStep;
    }

    const firstSlide = this.wrapperElement.querySelector('.blends-comparison-chart__slide');
    if (!(firstSlide instanceof HTMLElement)) {
      return fallbackStep;
    }

    const wrapperStyles = window.getComputedStyle(this.wrapperElement);
    const gapValue = parseFloat(wrapperStyles.columnGap || wrapperStyles.gap || '0');
    const safeGap = Number.isNaN(gapValue) ? 0 : gapValue;

    return firstSlide.offsetWidth + safeGap;
  }

  updateNavigationState() {
    if (!this.previousButton || !this.nextButton) return;

    if (!this.hasMultipleBlends) {
      this.previousButton.disabled = true;
      this.nextButton.disabled = true;
      this.updatePaginationState(0);
      return;
    }

    if (!this.swiperElement || !this.isNativeMobileSlider) {
      this.previousButton.disabled = true;
      this.nextButton.disabled = true;
      this.updatePaginationState(0);
      return;
    }

    const maxScrollLeft = Math.max(this.swiperElement.scrollWidth - this.swiperElement.clientWidth, 0);
    const currentScrollLeft = this.swiperElement.scrollLeft;
    const progress = maxScrollLeft > 0 ? currentScrollLeft / maxScrollLeft : 0;

    this.previousButton.disabled = currentScrollLeft <= 1;
    this.nextButton.disabled = currentScrollLeft >= (maxScrollLeft - 1);
    this.updatePaginationState(progress);
  }

  /**
   * @param {number} progressValue
   */
  updatePaginationState(progressValue) {
    if (!this.paginationElement) return;

    const totalSlides = Math.max(this.slideCount || 0, 1);
    const trackWidth = this.paginationElement.clientWidth;
    const indicatorWidth = trackWidth / totalSlides;
    const clampedProgress = Math.min(Math.max(progressValue, 0), 1);
    const indicatorOffset = Math.max(trackWidth - indicatorWidth, 0) * clampedProgress;

    this.paginationElement.style.setProperty(
      '--blends-comparison-chart-pagination-indicator-width',
      `${indicatorWidth}px`
    );
    this.paginationElement.style.setProperty(
      '--blends-comparison-chart-pagination-indicator-offset',
      `${indicatorOffset}px`
    );
  }

  resetRowHeights() {
    const rowSelectors = [
      '.blends-comparison-chart__title-row',
      '.blends-comparison-chart__attribute',
      '.blends-comparison-chart__button-row',
    ];

    rowSelectors.forEach((selector) => {
      this.querySelectorAll(selector).forEach((element) => {
        if (!(element instanceof HTMLElement)) return;

        element.style.minHeight = '';
        element.style.height = '';
      });
    });
  }

  syncRowHeights() {
    if (this.rowSyncFrame) {
      window.cancelAnimationFrame(this.rowSyncFrame);
    }

    this.rowSyncFrame = window.requestAnimationFrame(() => {
      this.rowSyncFrame = null;
      this.resetRowHeights();

      const slides = Array.from(this.querySelectorAll('.blends-comparison-chart__column'));

      if (!slides.length) return;

      const rowGroups = [
        '.blends-comparison-chart__title-row',
        '.blends-comparison-chart__attribute',
        '.blends-comparison-chart__button-row',
      ];

      rowGroups.forEach((selector) => {
        const maxRows = Math.max(
          ...slides.map((slide) => slide.querySelectorAll(selector).length),
          0
        );

        for (let rowIndex = 0; rowIndex < maxRows; rowIndex += 1) {
          const rowElements = slides
            .map((slide) => slide.querySelectorAll(selector)[rowIndex])
            .filter((element) => element instanceof HTMLElement);

          if (!rowElements.length) continue;

          const tallestRow = Math.max(...rowElements.map((element) => element.offsetHeight));

          rowElements.forEach((element) => {
            element.style.minHeight = `${tallestRow}px`;
            if (selector === '.blends-comparison-chart__attribute') {
              element.style.height = `${tallestRow}px`;
            }
          });
        }
      });
    });
  }

}

customElements.define('blends-comparison-chart-carousel', BlendsComparisonChartCarousel);
